import { Button, ChipMetadata, Tabs } from '@pmi/dsm-react';
import { SetStateAction, useCallback, useState } from 'react';
import { AlertErrorSolidIcon } from '@pmi/dsm-react/dist/components/icons';
import { toastWithAnalytics } from '../../../shared/analytics';
import {
  AccessRuleItems,
  ExclusionItem,
} from '../../../shared/models/AccessRule';
import { AgreementApiModel } from '../../../shared/models/DealModel';
import styles from './access-rules.module.scss';
import AccessRuleProductsTab from './AccessRuleProductsTab';
import {
  useAddExclusionMutation,
  useRemoveExclusionMutation,
  useToggleDomainAccessMutation,
} from './apiSlice';
import DomainAccessTab from './DomainAccessTab';
import ExclusionsTab from './ExclusionsTab';

interface AccessRuleCardProps {
  rule: AccessRuleItems;
  deal: AgreementApiModel;
  isEditMode: boolean;
  onEditModeChange: (editing: boolean) => void;
}

const editTabs = [
  { label: 'Products', value: 0 },
  { label: 'Add Exclusions', value: 1 },
  { label: 'Remove Exclusions', value: 2 },
  { label: 'Domain Access', value: 3 },
];

const getTabDisplayName = (tabIndex: number): string => {
  const tabLabels: Record<number, string> = {
    0: 'Products',
    1: 'Add Exclusions',
    2: 'Exclusions',
    3: 'Domain Access',
  };
  return tabLabels[tabIndex] || '';
};

function handleMutationError(error: any, fallbackMessage: string) {
  let errorMessage = fallbackMessage;
  try {
    if (error?.data?.Value) {
      const parsedError = JSON.parse(error.data.Value);
      errorMessage = parsedError.error || fallbackMessage;
    }
  } catch {
    // Use fallback message if JSON parsing fails
  }
  toastWithAnalytics(errorMessage, 'danger');
}

export default function AccessRuleCard({
  rule,
  deal,
  isEditMode,
  onEditModeChange,
}: AccessRuleCardProps) {
  const {
    domainName,
    accountNumber,
    notes,
    accessRuleType,
    name,
    providerNumber,
    createdByPersonId,
    id,
    isActive,
    priority,
  } = rule;

  const [activeTab, setActiveTab] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [pendingAdds, setPendingAdds] = useState<ExclusionItem[]>([]);
  const [pendingRemovals, setPendingRemovals] = useState<ExclusionItem[]>([]);
  const [pendingDomainAccessChange, setPendingDomainAccessChange] = useState<
    boolean | null
  >(null);
  const [emailTagsAdd, setEmailTagsAdd] = useState<string[]>([]);
  const [invalidEmailSetAdd, setInvalidEmailSetAdd] = useState<Set<string>>(
    new Set(),
  );
  const [emailTagsRemove, setEmailTagsRemove] = useState<string[]>([]);
  const [invalidEmailSetRemove, setInvalidEmailSetRemove] = useState<
    Set<string>
  >(new Set());

  const [addExclusion] = useAddExclusionMutation();
  const [removeExclusion] = useRemoveExclusionMutation();
  const [toggleDomainAccess] = useToggleDomainAccessMutation();

  const handlePendingChangesUpdate = useCallback(
    (hasChanges: boolean) => setHasPendingChanges(hasChanges),
    [],
  );

  const handleSave = async () => {
    if (isUploading || isSaving) return;
    setIsSaving(true);
    try {
      // Save Add Exclusions
      if (activeTab === 1 && pendingAdds.length > 0) {
        await addExclusion({
          accountNumber,
          providerNumber,
          body: { exclusions: pendingAdds },
        })
          .unwrap()
          .then(() => {
            toastWithAnalytics(
              `${pendingAdds.length} exclusion(s) added successfully`,
              'success',
            );
            setPendingAdds([]);
            setEmailTagsAdd([]);
            setInvalidEmailSetAdd(new Set());
            setHasPendingChanges(false);
            onEditModeChange(false);
            setActiveTab(0);
          })
          .catch((error) => {
            handleMutationError(
              error,
              'Failed to add exclusions. Please try again.',
            );
          });
      }

      // Save Remove Exclusions
      if (activeTab === 2 && pendingRemovals.length > 0) {
        await removeExclusion({
          accountNumber,
          providerNumber,
          body: { exclusionIds: pendingRemovals.map((x) => x.id) },
        })
          .unwrap()
          .then(() => {
            toastWithAnalytics(
              `${pendingRemovals.length} exclusion(s) removed successfully`,
              'success',
            );
            setPendingRemovals([]);
            setHasPendingChanges(false);
            onEditModeChange(false);
            setActiveTab(0);
          })
          .catch((error) => {
            handleMutationError(
              error,
              'Failed to remove exclusions. Please try again.',
            );
          });
      }

      // Save Domain Access
      if (activeTab === 3 && pendingDomainAccessChange !== null) {
        if (pendingDomainAccessChange === isActive) {
          toastWithAnalytics('No changes to save', 'warning');
          setIsSaving(false);
          return;
        }
        const newIsActive = pendingDomainAccessChange;
        const body = {
          id,
          accountNumber,
          providerNumber,
          isActive: newIsActive,
          createdByPersonId,
          notes,
          domainName,
          name,
          accessRuleType,
          priority,
        };
        await toggleDomainAccess({ body })
          .unwrap()
          .then(() => {
            const message = newIsActive
              ? `Domain access enabled for ${domainName}`
              : `Domain access disabled for ${domainName}`;
            toastWithAnalytics(message, 'success');
            setPendingDomainAccessChange(null);
            setHasPendingChanges(false);
            onEditModeChange(false);
            setActiveTab(0);
          })
          .catch(() => {
            toastWithAnalytics(
              'Could not update domain access. Please try again.',
              'danger',
            );
          });
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={`${styles.innerCard} mb-4`}>
      {/* Domain Header + Edit Button */}
      <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 mb-2">
        <div className="d-flex flex-column flex-md-row gap-2 flex-shrink-0">
          <div>{deal.dealName || deal.id}</div>
          <div className="d-none d-md-block">|</div>
          <div className="d-flex align-items-center gap-2">
            Domain: <span className={styles.domainText}>{domainName}</span>
            {!isActive && (
              <ChipMetadata
                icon={<AlertErrorSolidIcon />}
                text="Domain Access Disabled"
                variant="red"
              />
            )}
          </div>
        </div>

        {!isEditMode ? (
          <div className="d-grid d-md-block">
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                onEditModeChange(true);
                setActiveTab(0);
              }}
              titleText="Edit Domain"
            />
          </div>
        ) : (
          <div className="d-grid d-md-flex gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                onEditModeChange(false);
                setPendingAdds([]);
                setEmailTagsAdd([]);
                setInvalidEmailSetAdd(new Set());
                setPendingRemovals([]);
                setPendingDomainAccessChange(null);
                setActiveTab(0);
              }}
              titleText="Cancel"
            />
            {activeTab !== 0 && (
              <Button
                variant="primary"
                size="sm"
                disabled={isSaving || isUploading || !hasPendingChanges}
                onClick={handleSave}
                titleText={`Save ${getTabDisplayName(activeTab)} Changes`}
              />
            )}
          </div>
        )}
      </div>

      <hr className={styles.divider} />

      {!isEditMode ? (
        /* ===== VIEW MODE ===== */
        <>
          {/* Products Section (read-only) */}
          <AccessRuleProductsTab rule={rule} deal={deal} readOnly />

          {/* Exclusions Section (read-only) */}
          <div className={styles.excludedEmailContainer}>
            {rule?.exclusions?.length === 0 ? (
              <div className={styles.emailListHeader}>No Excluded emails</div>
            ) : (
              <div>
                <div className={`${styles.emailListHeader} mb-1`}>
                  Excluded emails:
                </div>
                <div className={styles.emailListContent}>
                  {rule?.exclusions?.map((x) => (
                    <div key={x.id}>
                      <hr />
                      <div
                        className={`${styles.emailListContainer} d-flex justify-content-between align-items-center`}
                      >
                        <div className={styles.emailListItem}>
                          {x.exclusionValue}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        /* ===== EDIT MODE ===== */
        <>
          <div className={styles.tabsContainer}>
            <Tabs
              labels={editTabs}
              onChange={(_e: any, newValue: SetStateAction<number>) => {
                setActiveTab(newValue);
                setHasPendingChanges(false);
              }}
              value={activeTab}
            />
          </div>

          <div className="mt-3">
            {/* Products Tab */}
            {activeTab === 0 && (
              <AccessRuleProductsTab
                rule={rule}
                deal={deal}
                onEditModeChange={onEditModeChange}
              />
            )}

            {/* Add Exclusions Tab */}
            {activeTab === 1 && (
              <ExclusionsTab
                rule={rule}
                dealId={String(deal.id)}
                onUploadingChange={setIsUploading}
                mode="add"
                pendingAdds={pendingAdds}
                pendingRemovals={pendingRemovals}
                onPendingAddsChange={setPendingAdds}
                onPendingRemovalsChange={setPendingRemovals}
                onPendingChangesUpdate={handlePendingChangesUpdate}
                emailTags={emailTagsAdd}
                invalidEmailSet={invalidEmailSetAdd}
                onEmailTagsChange={setEmailTagsAdd}
                onInvalidEmailSetChange={setInvalidEmailSetAdd}
              />
            )}

            {/* Remove Exclusions Tab */}
            {activeTab === 2 && (
              <ExclusionsTab
                rule={rule}
                dealId={String(deal.id)}
                onUploadingChange={setIsUploading}
                mode="remove"
                pendingAdds={pendingAdds}
                pendingRemovals={pendingRemovals}
                onPendingAddsChange={setPendingAdds}
                onPendingRemovalsChange={setPendingRemovals}
                onPendingChangesUpdate={handlePendingChangesUpdate}
                emailTags={emailTagsRemove}
                invalidEmailSet={invalidEmailSetRemove}
                onEmailTagsChange={setEmailTagsRemove}
                onInvalidEmailSetChange={setInvalidEmailSetRemove}
              />
            )}

            {/* Domain Access Tab */}
            {activeTab === 3 && (
              <DomainAccessTab
                rule={rule}
                pendingDomainAccessChange={pendingDomainAccessChange}
                onPendingDomainAccessChange={setPendingDomainAccessChange}
                onPendingChangesUpdate={handlePendingChangesUpdate}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
